/* Print-time layout for a rendered Healthlink message. Kept out of the component so the
 * stylesheet and the wrapper can be asserted directly — the way this breaks is silent.
 */

/*
 * Repeating page margins without a page margin (KIN-2029).
 *
 * Chrome and Edge draw their own header and footer — the document's title and date at the top, its
 * URL and page numbers at the bottom — inside the page margin. A printout is opened with
 * `window.open("")`, so its URL is `about:blank` and script cannot change it, and that string
 * appeared at the foot of every printed page and every "Save as PDF". The header was fixable by
 * giving the document a real title (see DEFAULT_PRINT_TITLE); the footer was not.
 *
 * `@page { margin: 0 }` leaves nowhere to draw them, so they are suppressed — and the whitespace
 * then has to come from the content. Body padding alone does not survive pagination: left and
 * right apply to every fragment, but top applies only to the first page and bottom only to the
 * last, so from page two the text would run into the printer's unprintable edge.
 *
 * A table's thead and tfoot repeat on every printed page. That is what carries the top and bottom
 * whitespace, and it is why the print document wraps the message in one (see PRINT_SPACER_CLASS).
 * The margin-box approach (`@page { @bottom-left … }`) was not an option: no browser we target
 * implements page margin boxes.
 */
export const PRINT_SPACER_CLASS = "kinesinPrintSpacer";
export const PRINT_MARGIN_VERTICAL = "12mm";
export const PRINT_MARGIN_HORIZONTAL = "8mm";

export const BASE_STYLES = `
      html, body { margin: 0; overflow: hidden; }
      body { padding: 24px; }
      @media print {
        @page { margin: 0; }
        html, body { overflow: visible; }
        body { padding: 0 ${PRINT_MARGIN_HORIZONTAL}; }
        table.${PRINT_SPACER_CLASS} { width: 100%; border-collapse: collapse; }
        /* Beats the \`td, th { padding: 4px 8px }\` the restyling applies to every cell: on the
           spacer's own cells that padding would widen the margin it is there to reproduce. */
        table.${PRINT_SPACER_CLASS} > thead > tr > td,
        table.${PRINT_SPACER_CLASS} > tfoot > tr > td,
        table.${PRINT_SPACER_CLASS} > tbody > tr > td {
          padding: 0;
          border: none;
        }
        table.${PRINT_SPACER_CLASS} > thead > tr > td,
        table.${PRINT_SPACER_CLASS} > tfoot > tr > td { height: ${PRINT_MARGIN_VERTICAL}; }
      }`;

/*
 * The spacer only exists to hold whitespace, so it is hidden from assistive technology and
 * contributes nothing on screen — it is emitted for the print document alone.
 */
export const wrapForPrint = (bodyHtml) => `
      <table class="${PRINT_SPACER_CLASS}" role="presentation" aria-hidden="true">
        <thead><tr><td></td></tr></thead>
        <tfoot><tr><td></td></tr></tfoot>
        <tbody><tr><td>${bodyHtml}</td></tr></tbody>
      </table>`;
